import { useMemo } from 'preact/hooks';
import cn from 'classnames';
import { EMPTY_ARRAY, generateId } from '../../utility';
import type { HTMLAttributes, AccessibleLabel, StandardSize } from '../../types';

type CircularProps = { type: 'circular'; size?: StandardSize };
type LinearProps = { type?: 'linear' | 'linear-rounded'; size?: never };
type AIProps = { type: 'ai'; labelPosition?: never; size?: never };
type SizeAndTypeProps = CircularProps | LinearProps | AIProps;

type Props = {
  labelPosition?: 'before' | 'after';
  layout?: 'column' | 'row';
  max?: number;
  value?: number;
  steps?: string[];
} & SizeAndTypeProps &
  AccessibleLabel &
  Omit<HTMLAttributes<HTMLProgressElement>, 'size' | 'max' | 'value'>;

const TYPES_WITH_STEPS = new Set(['linear', 'linear-rounded']);

export default function Progress({
  className,
  label,
  labelPosition = 'before',
  layout = 'column',
  max = 1,
  size = 'medium',
  steps = EMPTY_ARRAY,
  type = 'linear',
  value,
  ...props
}: Props) {
  const id = useMemo(() => `progress-${generateId()}`, []);
  const validMax = max < 0 ? 1 : max;
  const hasPassedValue = typeof value !== 'undefined';
  let validValue;
  let percent;

  if (hasPassedValue) {
    validValue = Math.max(0, Math.min(validMax, value));
    percent = validValue >= 0 ? Math.round((validValue / validMax) * 100) : 0;
  }

  const classList = cn('progress', className, {
    ['label-after']: label && labelPosition === 'after',
    ['layout-row']: layout === 'row',
  });

  // setting a key to force re-render between value updates; otherwise a truthy `value` won't update when a falsy one is provided
  return (
    <div className={classList}>
      {label && <label htmlFor={id}>{label}</label>}
      <progress
        key={value}
        id={id}
        max={validMax}
        value={validValue}
        className={cn({
          [size]: size !== 'medium',
          [type]: type !== 'linear',
        })}
        style={{ '--percent': percent }}
        data-icon={type === 'ai' ? 'ai' : undefined}
        {...props}
      />
      {TYPES_WITH_STEPS.has(type) && hasPassedValue && steps.length > 0 && (
        <ul className="progress-steps">
          {steps.map((step) => (
            <li key={step} className="label-small-plus">
              {step}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function LinearProgress(props: Omit<Extract<Props, LinearProps>, 'type'> & AccessibleLabel) {
  return <Progress type="linear" {...props} />;
}

export function CircularProgress(
  props: Omit<Extract<Props, CircularProps>, 'type'> & AccessibleLabel,
) {
  return <Progress type="circular" {...props} />;
}

export function AIProgress(
  props: Omit<Extract<Props, AIProps>, 'type' | 'size'> & AccessibleLabel,
) {
  return <Progress type="ai" {...props} />;
}
